import { zodResolver } from '@hookform/resolvers/zod'
import { AxiosError, isAxiosError } from 'axios'
import { TabsContent } from '../ui/tabs'
import {
  Building2,
  Edit,
  Mail,
  Phone,
  Plus,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Trash2,
  UserRound,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { GlobalFilterBar } from '../ui/GlobalFilterBar'

import { ConfirmationModal } from '../ui/ConfirmationModal'
import { FormError } from '../ui/FormError'
import { SearchFilter } from '../ui/SearchFilter'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { DiscreteLoader } from '../ui/discrete-loader'
import { GlobalPaginator } from '../ui/global-paginator'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

import {
  SupplierItem,
  SupplierPayload,
  supplierService,
} from '@/app/services/supplier.service'
import { useSessionStore } from '@/app/store/auth/auth.store'

const rfcPattern = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/

const supplierSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'El nombre del proveedor es obligatorio')
    .max(255, 'El nombre no puede superar los 255 caracteres'),
  contact_name: z
    .string()
    .trim()
    .max(255, 'El contacto no puede superar los 255 caracteres')
    .optional(),
  phone: z
    .string()
    .trim()
    .max(30, 'El teléfono no puede superar los 30 caracteres')
    .optional(),
  email: z
    .string()
    .trim()
    .max(255, 'El correo no puede superar los 255 caracteres')
    .refine((value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), 'Ingresa un correo válido')
    .optional(),
  rfc: z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .refine(
      (value) => value === '' || rfcPattern.test(value),
      'El RFC debe tener un formato mexicano válido de 12 o 13 caracteres',
    ),
  is_active: z.boolean(),
})

type SupplierFormValues = z.infer<typeof supplierSchema>
type SupplierField = keyof Pick<
  SupplierFormValues,
  'name' | 'contact_name' | 'phone' | 'email' | 'rfc'
>

interface ValidationErrorResponse {
  message?: string
  errors?: Partial<Record<SupplierField, string[]>>
}

const emptyForm: SupplierFormValues = {
  name: '',
  contact_name: '',
  phone: '',
  email: '',
  rfc: '',
  is_active: true,
}

const itemsPerPage = 9

export function SuppliersTab() {
  const [suppliers, setSuppliers] = useState<SupplierItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [refreshKey, setRefreshKey] = useState(0)
  const [pagination, setPagination] = useState({
    total: 0,
    from: 0,
    to: 0,
  })
  const [showModal, setShowModal] = useState(false)
  const [editingSupplier, setEditingSupplier] =
    useState<SupplierItem | null>(null)
  const [deleteSupplier, setDeleteSupplier] = useState<SupplierItem | null>(
    null,
  )
  const [statusSupplier, setStatusSupplier] = useState<SupplierItem | null>(
    null,
  )

  const canAction = useSessionStore((state) => state.canAction)
  const canCreate = canAction('purchases.supplier.create')
  const canEdit = canAction('purchases.supplier.edit')
  const canChangeStatus = canAction('purchases.supplier.status')
  const canDelete = canAction('purchases.supplier.delete')

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SupplierFormValues>({
    resolver: zodResolver(supplierSchema),
    mode: 'onChange',
    defaultValues: emptyForm,
  })

  const isActive = watch('is_active')

  useEffect(() => {
    let isMounted = true

    const loadSuppliers = async () => {
      setIsLoading(true)
      setLoadError(false)

      try {
        const response = await supplierService.getItems({
          search: search || undefined,
          is_active:
            status === 'all' ? undefined : status === 'active',
          page: currentPage,
          per_page: itemsPerPage,
        })

        if (!isMounted) return

        if (Array.isArray(response.data)) {
          setSuppliers(response.data)
          setPagination({
            total: response.data.length,
            from: response.data.length > 0 ? 1 : 0,
            to: response.data.length,
          })
        } else {
          setSuppliers(response.data.data)
          setPagination({
            total: response.data.total,
            from: response.data.from ?? 0,
            to: response.data.to ?? 0,
          })
        }
      } catch {
        if (!isMounted) return
        setSuppliers([])
        setPagination({ total: 0, from: 0, to: 0 })
        setLoadError(true)
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    void loadSuppliers()

    return () => {
      isMounted = false
    }
  }, [currentPage, refreshKey, search, status])

  const refresh = () => setRefreshKey((value) => value + 1)

  const openCreateModal = () => {
    setEditingSupplier(null)
    reset(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (supplier: SupplierItem) => {
    setEditingSupplier(supplier)
    reset({
      name: supplier.name,
      contact_name: supplier.contact_name,
      phone: supplier.phone,
      email: supplier.email,
      rfc: supplier.rfc ?? '',
      is_active: supplier.is_active,
    })
    setShowModal(true)
  }

  const applyServerValidation = (
    error: AxiosError<ValidationErrorResponse>,
  ): boolean => {
    const serverErrors = error.response?.data.errors
    if (!serverErrors) return false

    let applied = false
    for (const field of [
      'name',
      'contact_name',
      'phone',
      'email',
      'rfc',
    ] as SupplierField[]) {
      const message = serverErrors[field]?.[0]
      if (message) {
        setError(field, { type: 'server', message })
        applied = true
      }
    }

    return applied
  }

  const onSubmit = async (values: SupplierFormValues) => {
    const payload: SupplierPayload = {
      name: values.name.trim(),
      contact_name: values.contact_name?.trim() || '',
      phone: values.phone?.trim() || '',
      email: values.email?.trim().toLowerCase() || '',
      rfc: values.rfc || null,
      is_active: values.is_active,
    }

    try {
      if (editingSupplier) {
        await supplierService.updateItem(editingSupplier.id, payload)
        toast.success('Proveedor actualizado exitosamente')
      } else {
        await supplierService.createItem(payload)
        toast.success('Proveedor creado exitosamente')
      }

      setShowModal(false)
      refresh()
    } catch (error) {
      if (
        isAxiosError<ValidationErrorResponse>(error) &&
        applyServerValidation(error)
      ) {
        return
      }

      const message =
        isAxiosError<ValidationErrorResponse>(error)
          ? error.response?.data?.message
          : undefined
      toast.error(message || 'No fue posible guardar el proveedor')
    }
  }

  const executeStatusChange = async () => {
    if (!statusSupplier) return

    try {
      await supplierService.updateStatus(
        statusSupplier.id,
        !statusSupplier.is_active,
      )
      toast.success('Estado del proveedor actualizado')
      refresh()
    } catch {
      toast.error('No fue posible actualizar el estado del proveedor')
    } finally {
      setStatusSupplier(null)
    }
  }

  const executeDelete = async () => {
    if (!deleteSupplier) return

    try {
      await supplierService.deleteItem(deleteSupplier.id)
      toast.success('Proveedor eliminado exitosamente')
      if (suppliers.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1)
      } else {
        refresh()
      }
    } catch (error) {
      const message =
        isAxiosError<ValidationErrorResponse>(error)
          ? error.response?.data?.message
          : undefined
      toast.error(message || 'No fue posible eliminar el proveedor')
    } finally {
      setDeleteSupplier(null)
    }
  }

  return (
    <TabsContent value="suppliers" className="space-y-4">
      <div className="space-y-3">
  <p className="text-sm text-header-foreground">
    {pagination.total} proveedores registrados
  </p>

  <GlobalFilterBar
    show={[]}
    values={{}}
    onChange={() => {}}
  >
    <div className="flex w-full flex-nowrap items-center gap-3">
      <SearchFilter
        className="h-10 w-[300px] shrink-0"
        placeholder="Buscar proveedor..."
        onSearch={(value) => {
          setSearch(value)
          setCurrentPage(1)
        }}
      />

      <Select
        value={status}
        onValueChange={(value) => {
          setStatus(value)
          setCurrentPage(1)
        }}
      >
        <SelectTrigger className="h-10 w-[180px] shrink-0">
          <SelectValue placeholder="Estado" />
        </SelectTrigger>

        <SelectContent>
          <SelectItem value="all">Todos</SelectItem>
          <SelectItem value="active">Activos</SelectItem>
          <SelectItem value="inactive">Inactivos</SelectItem>
        </SelectContent>
      </Select>

      {canCreate && (
        <Button
          onClick={openCreateModal}
          className="ml-auto h-10 shrink-0"
        >
          <Plus className="mr-2 h-4 w-4" />
          Agregar Proveedor
        </Button>
      )}
    </div>
  </GlobalFilterBar>
</div>
          
      {isLoading ? (
        <div className="min-h-64 rounded-lg border border-slate-200 bg-white">
          <DiscreteLoader text="Cargando proveedores..." />
        </div>
      ) : loadError ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <Building2 className="mb-3 h-10 w-10 text-red-300" />
          <p className="font-medium text-red-800">
            No fue posible cargar los proveedores
          </p>
          <Button className="mt-4" variant="outline" onClick={refresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Reintentar
          </Button>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-table-border bg-table-bg p-6 text-center">
          <Building2 className="mb-3 h-10 w-10 text-card-description" />
          <p className="font-medium text-table-foreground">
            No se encontraron proveedores
          </p>
          <p className="mt-1 text-sm text-card-description">
            Ajusta los filtros o registra el primer proveedor.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {suppliers.map((supplier) => (
              <article
                key={supplier.id}
                className="rounded-lg border border-table-border bg-card p-6 shadow-sm transition-colors hover:bg-table-row-hover"
              >
                <div className="mb-5 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground">
                      {supplier.name}
                    </h3>
                    {supplier.rfc && (
                      <p className="mt-1 font-mono text-xs text-card-foreground">
                        RFC: {supplier.rfc}
                      </p>
                    )}
                  </div>
                 <Badge
                          className={
                            supplier.is_active
                              ? 'bg-[#DCFCE6] text-[#008438]'
                              : 'bg-[#FFE2E2] text-[#C10008]'
                          }
                        >
                          {supplier.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                </div>

                <dl className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <UserRound className="mt-0.5 h-4 w-4 text-slate-400" />
                    <div>
                      <dt className="text-xs text-card-foreground">Contacto</dt>
                      <dd className="font-medium text-card-foreground">
                        {supplier.contact_name}
                      </dd>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="mt-0.5 h-4 w-4 text-slate-400" />
                    <div>
                      <dt className="text-xs text-card-foreground">Teléfono</dt>
                      <dd className="text-card-foreground">{supplier.phone}</dd>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-4 w-4 text-slate-400" />
                    <div className="min-w-0">
                      <dt className="text-xs text-card-description">Correo</dt>
                      <dd className="truncate text-card-foreground">
                        {supplier.email}
                      </dd>
                    </div>
                  </div>
                </dl>

                {(canChangeStatus || canEdit || canDelete) && (
                  <div className="mt-5 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
                    {canChangeStatus && (
                      <button
                        type="button"
                        className="rounded p-1.5 text-slate-500 transition-colors hover:bg-table-row-hover"
                        title={
                          supplier.is_active ? 'Desactivar' : 'Activar'
                        }
                        onClick={() => setStatusSupplier(supplier)}
                      >
                        {supplier.is_active ? (
                          <ToggleRight className="h-5 w-5 text-blue-500" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </button>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        className="rounded p-1.5 text-slate-500 transition-colors hover:bg-table-row-hover"
                        title="Editar"
                        onClick={() => openEditModal(supplier)}
                      >
                        <Edit className="h-4 w-4 text-white "/>
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50"
                        title="Eliminar"
                        onClick={() => setDeleteSupplier(supplier)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>

          <div className="mt-6 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm [&>div]:border-t-0 [&>div]:rounded-lg">
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={setCurrentPage}
            />
          </div>
        </>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingSupplier ? 'Editar Proveedor' : 'Agregar Nuevo Proveedor'}
            </DialogTitle>
            <DialogDescription>
              Captura los datos de contacto y fiscales del proveedor.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4 py-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div>
              <label className="mb-1 block text-sm font-medium text-card-foreground">
                Nombre del Proveedor <span className="text-red-500">*</span>
              </label>
              <Input
                {...register('name')}
                placeholder="Ej: Laboratorios Pisa"
              />
              <FormError message={errors.name?.message} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-card-foreground">
                  Nombre de Contacto
                </label>
                <Input
                  {...register('contact_name')}
                  placeholder="Ej: Juan Pérez"
                />
                <FormError message={errors.contact_name?.message} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-card-foreground">
                  Teléfono
                </label>
                <Input {...register('phone')} placeholder="Ej: 555-1234" />
                <FormError message={errors.phone?.message} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-card-foreground">
                  Correo Electrónico
                </label>
                <Input
                  type="email"
                  {...register('email')}
                  placeholder="Ej: ventas@proveedor.com"
                />
                <FormError message={errors.email?.message} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-card-foreground">
                  RFC
                </label>
                <Input
                  {...register('rfc')}
                  className="uppercase"
                  placeholder="Ej: ABC010203XYZ"
                  maxLength={13}
                />
                <FormError message={errors.rfc?.message} />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="supplier-is-active"
                checked={isActive}
                onCheckedChange={(checked) =>
                  setValue('is_active', checked === true, {
                    shouldValidate: true,
                  })
                }
              />
              <label
                htmlFor="supplier-is-active"
                className="text-sm font-medium text-card-foreground"
              >
                Proveedor activo
              </label>
            </div>

            <DialogFooter className="pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Guardando...
                  </span>
                ) : (
                  'Guardar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={statusSupplier !== null}
        onClose={() => setStatusSupplier(null)}
        onConfirm={() => void executeStatusChange()}
        preset="toggleStatus"
        itemName="proveedor"
      />

      <ConfirmationModal
        isOpen={deleteSupplier !== null}
        onClose={() => setDeleteSupplier(null)}
        onConfirm={() => void executeDelete()}
        preset="delete"
        itemName="proveedor"
      />
    </TabsContent>
  )
}
